---
id: pipeline-failure-diagnosis
title: Pipeline Failure Diagnosis
category: cicd
difficulty: intermediate
tags:
  - pipeline
  - debugging
  - ci
  - failure
  - logs
  - github-actions
description: >
  Analyzes CI/CD pipeline failure logs, identifies root cause, and suggests
  targeted fixes with confidence levels.
whenToUse: >
  When a CI/CD pipeline fails and you need quick, structured root-cause
  analysis instead of manually scrolling through logs.
variables:
  - name: platform
    desc: CI platform (GitHub Actions, GitLab CI, Jenkins, etc.)
  - name: repository
    desc: Repository name
  - name: branch
    desc: Branch that triggered the pipeline
  - name: trigger
    desc: What triggered the run (push, PR, schedule, manual)
  - name: failed_stage
    desc: Name of the failed pipeline stage
  - name: logs
    desc: Relevant failure log output (last 100-200 lines)
tips:
  - Include the last 100-200 lines of logs — enough context without overwhelming the model.
  - Mention any recent changes (dependency updates, config changes) for better diagnosis.
  - For flaky tests, include logs from both passing and failing runs if available.
---

You are a senior CI/CD engineer analyzing a pipeline failure.

## Pipeline Context
- Platform: {{platform}}
- Repository: {{repository}}
- Branch: {{branch}}
- Trigger: {{trigger}}
- Failed Stage: {{failed_stage}}

## Failure Logs
```
{{logs}}
```

## Task
1. Identify the root cause of the failure
2. Classify the failure type (build, test, deploy, infra, dependency, config)
3. Suggest a fix with step-by-step instructions
4. Rate your confidence (high/medium/low)

## Output Format (JSON)
{
  "root_cause": "string — one-sentence summary",
  "failure_type": "build | test | deploy | infra | dependency | config",
  "details": "string — detailed explanation",
  "fix": {
    "steps": ["step 1", "step 2"],
    "files_to_modify": ["path/to/file"],
    "commands": ["command to run"]
  },
  "confidence": "high | medium | low",
  "similar_issues": "string — known patterns this matches"
}

<!-- EXAMPLE_OUTPUT -->

{
  "root_cause": "Node.js version mismatch — project requires Node 20 but runner uses Node 18",
  "failure_type": "config",
  "details": "The build step fails with 'SyntaxError: Unexpected token ??=' which indicates the nullish assignment operator is not supported in Node 18. The .nvmrc file specifies Node 20.x but the CI runner uses the default Node 18 image.",
  "fix": {
    "steps": [
      "Update the CI workflow to use Node 20",
      "Add explicit node version setup step"
    ],
    "files_to_modify": [".github/workflows/ci.yml"],
    "commands": []
  },
  "confidence": "high",
  "similar_issues": "Common Node.js version drift between local dev and CI environments"
}
